/**
 * One result row as the gram summary reads it: what the scan estimated, and what the row holds
 * when the user adds it (the same number, unless they changed it on the portion screen).
 */
export interface GramEditRow {
  aiEstimatedGrams: number;
  estimatedGrams: number;
  /** set when the user ticked "Weighed with peel" (#5544); the row's grams are then the eaten part */
  weighedWithRefuse?: { percent: number };
}

/** the gram numbers `meal_photo_scan_added` sends (#6808) */
export interface ScanGramEdits {
  /** the scan's own grams of the added rows, in whole grams */
  aiGramsTotal: number;
  /** the grams the user added those rows with, in whole grams */
  savedGramsTotal: number;
  /** how many of those rows the user changed, compared in whole grams as the row's grams line does */
  rowsGramsChanged: number;
  /** rows left out of the three numbers above because they were weighed with their peel */
  rowsWithPeel: number;
}

/**
 * Sums up how far the user corrected the scan's grams on the rows they add. savedGramsTotal ÷
 * aiGramsTotal over many scans says how far real users correct the scanner, and in which direction
 * (design-docs/meal-photo-scanner.md, "Measuring it").
 *
 * A row weighed with its peel is left out: its saved weight is the part eaten, while the scan
 * estimated what the photo shows, so the two are different quantities and comparing them would read
 * as a correction nobody made.
 */
export function summarizeScanGramEdits(rows: GramEditRow[]): ScanGramEdits {
  let aiGrams = 0;
  let savedGrams = 0;
  let rowsGramsChanged = 0;
  let rowsWithPeel = 0;
  for (const row of rows) {
    if (row.weighedWithRefuse) {
      rowsWithPeel++;
      continue;
    }
    aiGrams += row.aiEstimatedGrams;
    savedGrams += row.estimatedGrams;
    if (Math.round(row.estimatedGrams) !== Math.round(row.aiEstimatedGrams)) {
      rowsGramsChanged++;
    }
  }
  return { aiGramsTotal: Math.round(aiGrams), savedGramsTotal: Math.round(savedGrams), rowsGramsChanged, rowsWithPeel };
}
